// Settings modal — keyboard rebind + misc. Accessible from the pause
// menu `⚙ SETTINGS` button. Uses the central keybinds store.

import {
  ACTION_ORDER,
  ACTION_LABELS,
  ACTION_ICONS,
  DEFAULT_BINDINGS,
  getBindings,
  setPrimary,
  resetToDefaults,
  formatKeyCode,
  isModifierOnly,
  type KeyAction,
} from '../settings/keybinds';
// Themes intentionally not surfaced in Settings (playtest 2026-04-28).
// `?theme=` URL flag remains the lone entry point for power users.
// Art-pack picker removed 2026-04-20 — game ships a single definitive
// style (roguelike). `setActivePack` import dropped; if a legacy URL
// carries `?pack=<id>`, `getActivePack()` ignores unknown ids and falls
// back to the flagship.
// DEBUG section is dev-only — dynamic-imported below when
// `import.meta.env.DEV` is true, tree-shaken otherwise. No static
// import here so the prod bundle never sees it.
import { HUD_TOGGLES, isHudVisible, setHudVisible } from './hud_visibility';
// `PALETTE` was used by the old pack-preview helpers; no longer needed.
import {
  SFX,
  SONGS,
  setMusicVolume,
  setSfxVolume,
  getMusicVolume,
  getSfxVolume,
  resumeAudio,
  testBeep,
  audioDiag,
  type SongId,
} from '../audio/synth';

import {
  getPrefs,
  setPref,
  resetSection,
  onPrefsChange,
  type MotionTier,
  type DensityTier,
  type PerfPreset,
  type BloomMode,
  type PixelRatioMode,
  type DamageNumbersMode,
} from '../settings/prefs';
import { isTouchDevice } from './touch';
import { openTouchLayoutEditor } from './touch_layout_editor';

const NAME_KEY = 'smp.username';
const INTRO_KEY = 'smp.introSeenV1';

export type SettingsCallbacks = {
  onReplayIntro?: () => void;
};

export function openSettings(
  callbacks: SettingsCallbacks = {},
  onClose?: () => void,
): () => void {
  // Opening settings is itself a user gesture — kick the AudioContext
  // out of suspended so any volume/preview tweaks the user makes
  // actually take audible effect immediately.
  resumeAudio();
  console.info('[settings] open — audio diag:', audioDiag());
  // Pref-change subscriptions held by tier-select widgets so they can
  // repaint when the user clicks "Restore Defaults". Drained on close.
  const prefsUnsubs: Array<() => void> = [];
  const subscribeRepaint = (repaint: () => void) => {
    prefsUnsubs.push(onPrefsChange(repaint));
  };
  ensureSettingsCss();
  const overlay = document.createElement('div');
  overlay.className = 'ui interactive smp-settings-overlay';
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(2,6,23,.93);
    z-index: 9999;
    display: flex; flex-direction: column;
    align-items: center; justify-content: flex-start;
    color: #e5e7eb;
    font: 700 14px system-ui, sans-serif;
    padding: 32px 20px;
    overflow-y: auto;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font: 900 28px system-ui; letter-spacing: 4px; margin-bottom: 18px;
    color: #fde047;
    text-shadow: 0 0 18px rgba(253,224,71,.35);
  `;
  title.textContent = '⚙ SETTINGS';
  overlay.appendChild(title);

  // Tabbed layout — replaces the 1300px scroll. Top tab strip picks
  // the active category; body shows that category's sections only.
  type TabId = 'audio' | 'controls' | 'display' | 'profile' | 'help';
  const TAB_LABELS: Array<{ id: TabId; label: string; icon: string }> = [
    { id: 'audio',    label: 'AUDIO',    icon: '🔊' },
    { id: 'controls', label: 'CONTROLS', icon: '⌨️' },
    { id: 'display',  label: 'DISPLAY',  icon: '🎚️' },
    { id: 'profile',  label: 'PROFILE',  icon: '👤' },
    { id: 'help',     label: 'HELP',     icon: '❓' },
  ];
  const tabStrip = document.createElement('div');
  tabStrip.style.cssText = `
    display: flex; gap: 6px; flex-wrap: wrap; justify-content: center;
    margin-bottom: 22px; max-width: 600px;
  `;
  const tabBody = document.createElement('div');
  tabBody.style.cssText = `
    display: flex; flex-direction: column; align-items: center;
    width: 100%;
  `;
  let activeTab: TabId = 'audio';
  const tabButtons: Array<{ id: TabId; el: HTMLButtonElement }> = [];
  function paintTabs(): void {
    for (const t of tabButtons) {
      const isActive = t.id === activeTab;
      t.el.style.background = isActive ? 'rgba(253,224,71,.18)' : 'rgba(15,23,42,.55)';
      t.el.style.color = isActive ? '#fde047' : '#cbd5e1';
      t.el.style.borderColor = isActive ? '#fde047' : 'rgba(71,85,105,.5)';
    }
    const sections = tabBody.querySelectorAll<HTMLElement>('[data-tab]');
    sections.forEach((s) => {
      s.style.display = s.dataset.tab === activeTab ? '' : 'none';
    });
  }
  for (const t of TAB_LABELS) {
    const btn = document.createElement('button');
    btn.style.cssText = `
      padding: 9px 16px;
      border-radius: 10px;
      border: 1.5px solid rgba(71,85,105,.5);
      background: rgba(15,23,42,.55);
      color: #cbd5e1;
      font: 800 11px system-ui, sans-serif;
      letter-spacing: 1.5px;
      cursor: pointer;
      transition: background .14s, color .14s, border-color .14s;
    `;
    btn.innerHTML = `<span style="margin-right:6px;">${t.icon}</span>${t.label}`;
    btn.addEventListener('click', () => {
      activeTab = t.id;
      paintTabs();
    });
    tabStrip.appendChild(btn);
    tabButtons.push({ id: t.id, el: btn });
  }
  overlay.appendChild(tabStrip);
  overlay.appendChild(tabBody);

  // Helper that re-targets the original `overlay.appendChild(section)`
  // calls (kept inline below for diff minimality) into the matching
  // tab body slot. We assign a data-tab attribute so paintTabs can
  // toggle visibility without re-querying by section identity.
  function mountInTab(tab: TabId, section: HTMLElement): void {
    section.dataset.tab = tab;
    tabBody.appendChild(section);
  }

  // ---- Toast (shared) ----
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: absolute; top: 90px; left: 50%;
    transform: translateX(-50%);
    padding: 8px 18px;
    background: rgba(15,23,42,.9);
    border: 1px solid #fde047;
    border-radius: 10px;
    font: 800 12px system-ui; letter-spacing: 1.5px;
    color: #fde047;
    opacity: 0; pointer-events: none;
    transition: opacity .2s;
  `;
  overlay.appendChild(toast);
  let toastTimer = 0;
  function flashToast(msg: string, color = '#fde047') {
    toast.textContent = msg;
    toast.style.color = color;
    toast.style.borderColor = color;
    toast.style.opacity = '1';
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => { toast.style.opacity = '0'; }, 1800);
  }


  // ---- Section: AUDIO ----
  const audioSection = document.createElement('div');
  audioSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const audioTitle = document.createElement('div');
  audioTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
    display: flex; align-items: center; justify-content: space-between;
  `;
  audioTitle.innerHTML = `<span>AUDIO</span><span style="font:700 9px system-ui;color:#64748b;letter-spacing:1.5px;">VOLUME · SOUNDTRACK</span>`;
  audioSection.appendChild(audioTitle);

  // Slider helper — builds a labeled range input with a live percent
  // readout that calls back on input. Returns the wrapper element.
  function buildVolumeRow(
    label: string,
    initial: number,
    onChange: (v: number) => void,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; gap: 14px;
      padding: 8px 4px;
      font: 800 12px system-ui; letter-spacing: .5px;
    `;
    const lbl = document.createElement('div');
    lbl.style.cssText = `width: 100px; color: #cbd5e1;`;
    lbl.textContent = label;
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.value = String(Math.round(initial * 100));
    slider.style.cssText = `
      flex: 1; accent-color: #fde047; cursor: pointer;
    `;
    const readout = document.createElement('div');
    readout.style.cssText = `
      width: 48px; text-align: right;
      font: 800 12px system-ui; color: #fde047;
    `;
    readout.textContent = `${slider.value}%`;
    slider.addEventListener('input', () => {
      const v = parseInt(slider.value, 10) / 100;
      readout.textContent = `${slider.value}%`;
      onChange(v);
    });
    row.appendChild(lbl);
    row.appendChild(slider);
    row.appendChild(readout);
    return row;
  }

  audioSection.appendChild(buildVolumeRow('Music', getMusicVolume(), setMusicVolume));
  audioSection.appendChild(buildVolumeRow('SFX',   getSfxVolume(),   setSfxVolume));

  // Diagnostic: Test Beep + Log Audio Diag buttons. Dev-only — gated
  // on the build-time `import.meta.env.DEV` flag. Prod builds strip
  // this entire block.
  const debugAudio = import.meta.env.DEV;
  const testRow = document.createElement('div');
  testRow.style.cssText = `
    display: flex; align-items: center; gap: 12px;
    padding: 6px 4px;
  `;
  const testBtn = document.createElement('button');
  testBtn.style.cssText = `
    padding: 8px 16px;
    background: rgba(253,224,71,.12);
    border: 1px solid #fde047;
    border-radius: 8px;
    color: #fde047;
    font: 800 11px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  testBtn.textContent = '🔊 TEST BEEP';
  testBtn.addEventListener('click', () => {
    resumeAudio();
    testBeep();
    console.info('[settings] testBeep clicked — diag:', audioDiag());
    flashToast('TEST BEEP FIRED', '#fde047');
  });
  const diagBtn = document.createElement('button');
  diagBtn.style.cssText = `
    padding: 8px 16px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #cbd5e1;
    font: 800 11px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  diagBtn.textContent = '🩺 LOG AUDIO DIAG';
  diagBtn.addEventListener('click', () => {
    const d = audioDiag();
    console.info('[audio diag]', d);
    flashToast('DIAG IN CONSOLE', '#94a3b8');
  });
  testRow.appendChild(testBtn);
  testRow.appendChild(diagBtn);
  if (debugAudio) audioSection.appendChild(testRow);

  // Tempo multiplier — type a number like 1.0, 1.5, 0.75 to scale the
  // active song's BPM live. Used for dialing in the right feel without
  // editing songs.ts and reloading.
  const tempoRow = document.createElement('div');
  tempoRow.style.cssText = `
    display: flex; align-items: center; gap: 14px;
    padding: 8px 4px;
    font: 800 12px system-ui; letter-spacing: .5px;
  `;
  const tempoLbl = document.createElement('div');
  tempoLbl.style.cssText = `width: 100px; color: #cbd5e1;`;
  tempoLbl.textContent = 'Tempo ×';
  const tempoInput = document.createElement('input');
  tempoInput.type = 'number';
  tempoInput.min = '0.25';
  tempoInput.max = '4';
  tempoInput.step = '0.05';
  tempoInput.value = String(SFX.music.getTempoScale());
  tempoInput.style.cssText = `
    width: 90px; padding: 6px 10px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #fde047;
    font: 800 13px system-ui; letter-spacing: .5px;
    outline: none;
  `;
  const tempoHint = document.createElement('div');
  tempoHint.style.cssText = `
    flex: 1; font: 600 10px system-ui; color: #64748b; letter-spacing: .3px;
  `;
  function refreshTempoHint() {
    const scale = parseFloat(tempoInput.value);
    if (!isFinite(scale) || scale <= 0) {
      tempoHint.textContent = 'enter a positive number (0.25–4)';
      tempoHint.style.color = '#ef4444';
      return;
    }
    // Show resulting BPM for each song so the player sees the math.
    const bpms = (Object.keys(SONGS) as SongId[])
      .map(id => `${SONGS[id].bpm * scale | 0}`)
      .join(' / ');
    tempoHint.textContent = `→ ${bpms} BPM (plaza/classic/tdm/pve/race)`;
    tempoHint.style.color = '#64748b';
  }
  tempoInput.addEventListener('input', () => {
    const v = parseFloat(tempoInput.value);
    if (isFinite(v) && v > 0) SFX.music.setTempoScale(v);
    refreshTempoHint();
  });
  refreshTempoHint();
  tempoRow.appendChild(tempoLbl);
  tempoRow.appendChild(tempoInput);
  tempoRow.appendChild(tempoHint);
  audioSection.appendChild(tempoRow);

  // Soundtrack preview row — one button per song. Clicking any button
  // calls SFX.music.previewSong which suppresses the per-frame
  // auto-selector for ~12s so the preview isn't immediately overridden.
  const songLabel = document.createElement('div');
  songLabel.style.cssText = `
    margin-top: 14px;
    font: 900 10px system-ui; letter-spacing: 2px;
    color: #94a3b8;
  `;
  songLabel.textContent = 'PREVIEW SOUNDTRACK';
  audioSection.appendChild(songLabel);

  const songRow = document.createElement('div');
  songRow.style.cssText = `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
    margin-top: 8px;
  `;
  const SONG_LABELS: Record<SongId, { name: string; tagline: string; tint: string }> = {
    plaza:   { name: 'Plaza',   tagline: 'Lobby · F maj', tint: '#fde047' },
    classic: { name: 'Classic', tagline: 'A min · 110',   tint: '#60a5fa' },
    tdm:     { name: 'TDM',     tagline: 'D min · 124',   tint: '#fb923c' },
    pve:     { name: 'PvE',     tagline: 'Phrygian · 78', tint: '#a78bfa' },
    race:    { name: 'Race',    tagline: 'E maj · 140',   tint: '#34d399' },
  };
  for (const id of Object.keys(SONGS) as SongId[]) {
    const meta = SONG_LABELS[id];
    const btn = document.createElement('button');
    btn.style.cssText = `
      display: flex; flex-direction: column; gap: 2px;
      padding: 8px 6px;
      background: rgba(30,41,59,.55);
      border: 1px solid rgba(71,85,105,.5);
      border-radius: 8px;
      cursor: pointer;
      font: inherit; color: #e5e7eb;
      transition: transform .12s, border-color .12s;
    `;
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = meta.tint;
      btn.style.transform = 'translateY(-1px)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(71,85,105,.5)';
      btn.style.transform = '';
    });
    const n = document.createElement('div');
    n.style.cssText = `font: 900 12px system-ui; letter-spacing: .5px; color:${meta.tint};`;
    n.textContent = '▶ ' + meta.name.toUpperCase();
    const tag = document.createElement('div');
    tag.style.cssText = `font: 600 9px system-ui; color: #94a3b8; letter-spacing: .3px;`;
    tag.textContent = meta.tagline;
    btn.appendChild(n);
    btn.appendChild(tag);
    btn.addEventListener('click', () => {
      SFX.music.previewSong(id, 15000);
      flashToast(`PREVIEW: ${meta.name.toUpperCase()}`, meta.tint);
    });
    songRow.appendChild(btn);
  }
  audioSection.appendChild(songRow);

  const previewHint = document.createElement('div');
  previewHint.style.cssText = `
    margin-top: 10px;
    font: 600 10px system-ui; color: #64748b; letter-spacing: .3px;
  `;
  previewHint.textContent = 'Preview holds for 15 seconds, then auto-selection resumes.';
  audioSection.appendChild(previewHint);

  mountInTab('audio', audioSection);

  // ---- Section: KEYBOARD ----
  const kbSection = document.createElement('div');
  kbSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const kbTitle = document.createElement('div');
  kbTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  kbTitle.textContent = 'KEYBOARD';
  kbSection.appendChild(kbTitle);

  const rowsContainer = document.createElement('div');
  rowsContainer.style.cssText = `display: flex; flex-direction: column; gap: 4px;`;
  kbSection.appendChild(rowsContainer);

  let listening: { action: KeyAction; row: HTMLDivElement } | null = null;

  function renderRows() {
    rowsContainer.innerHTML = '';
    const bindings = getBindings();
    for (const action of ACTION_ORDER) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; gap: 14px;
        padding: 9px 12px;
        border-radius: 8px;
        background: rgba(30,41,59,.5);
        border: 1px solid rgba(71,85,105,.3);
      `;
      const icon = document.createElement('div');
      icon.style.cssText = `width: 24px; text-align: center; font-size: 18px;`;
      icon.textContent = ACTION_ICONS[action];
      const label = document.createElement('div');
      label.style.cssText = `flex: 1; font: 800 12px system-ui; letter-spacing: .5px;`;
      label.textContent = ACTION_LABELS[action];

      const keys = bindings[action];
      const primary = keys[0] ?? '';
      const secondary = keys[1] ?? '';

      const keyBadge = (code: string, dim = false) => {
        const b = document.createElement('div');
        b.style.cssText = `
          min-width: 54px;
          padding: 5px 10px;
          background: ${dim ? 'rgba(71,85,105,.35)' : 'rgba(253,224,71,.15)'};
          border: 1px solid ${dim ? '#475569' : '#fde047'};
          border-radius: 6px;
          font: 800 11px system-ui; letter-spacing: 1px;
          color: ${dim ? '#94a3b8' : '#fde047'};
          text-align: center;
        `;
        b.textContent = code ? formatKeyCode(code) : '—';
        return b;
      };

      const primaryBadge = keyBadge(primary);
      const secondaryBadge = keyBadge(secondary, true);

      const rebindBtn = document.createElement('button');
      rebindBtn.style.cssText = `
        padding: 6px 14px;
        background: rgba(15,23,42,.8);
        border: 1px solid #475569;
        border-radius: 8px;
        font: 800 11px system-ui; letter-spacing: 1px;
        color: #e5e7eb;
        cursor: pointer;
      `;
      rebindBtn.textContent = 'REBIND';
      rebindBtn.addEventListener('click', () => startListening(action, row));

      row.appendChild(icon);
      row.appendChild(label);
      row.appendChild(primaryBadge);
      row.appendChild(secondaryBadge);
      row.appendChild(rebindBtn);
      rowsContainer.appendChild(row);
    }
  }

  function startListening(action: KeyAction, row: HTMLDivElement) {
    if (listening) return;
    listening = { action, row };
    row.style.background = 'rgba(253,224,71,.12)';
    row.style.borderColor = '#fde047';
    const rebindBtn = row.querySelector<HTMLButtonElement>('button')!;
    rebindBtn.textContent = 'PRESS A KEY…';
    rebindBtn.style.color = '#fde047';
  }

  function cancelListening() {
    if (!listening) return;
    listening = null;
    renderRows();
  }

  // Global capture while listening
  const onCapture = (e: KeyboardEvent) => {
    if (!listening) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'Escape') {
      cancelListening();
      flashToast('REBIND CANCELED', '#94a3b8');
      return;
    }
    if (isModifierOnly(e.code)) {
      flashToast('MODIFIERS ARE NOT BINDABLE', '#ef4444');
      return;
    }
    const action = listening.action;
    const { reassignedFrom } = setPrimary(action, e.code);
    listening = null;
    renderRows();
    if (reassignedFrom) {
      flashToast(`REASSIGNED FROM ${ACTION_LABELS[reassignedFrom].toUpperCase()}`, '#fb923c');
    } else {
      flashToast(`BOUND ${formatKeyCode(e.code)}`, '#86efac');
    }
  };
  addEventListener('keydown', onCapture, true);

  // Reset button
  const resetBtn = document.createElement('button');
  resetBtn.style.cssText = `
    margin-top: 14px; padding: 7px 16px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #94a3b8;
    font: 800 11px system-ui; letter-spacing: 1.5px;
    cursor: pointer;
  `;
  resetBtn.textContent = '↺ RESET TO DEFAULTS';
  resetBtn.addEventListener('click', () => {
    resetToDefaults();
    renderRows();
    flashToast('BINDINGS RESET', '#86efac');
  });
  kbSection.appendChild(resetBtn);

  mountInTab('controls', kbSection);
  renderRows();

  // ---- Section: PROFILE ----
  const profileSection = document.createElement('div');
  profileSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const profTitle = document.createElement('div');
  profTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  profTitle.textContent = 'PROFILE';
  profileSection.appendChild(profTitle);

  const profileRow = document.createElement('div');
  profileRow.style.cssText = `display: flex; align-items: center; gap: 14px;`;
  const currentName = document.createElement('div');
  currentName.style.cssText = `flex: 1; font: 800 12px system-ui; letter-spacing: .5px;`;
  const saved = localStorage.getItem(NAME_KEY) ?? '(auto)';
  currentName.textContent = `Name: ${saved}`;

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.maxLength = 16;
  nameInput.placeholder = 'New name';
  nameInput.style.cssText = `
    width: 160px; padding: 7px 12px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #e5e7eb;
    font: 700 12px system-ui;
    outline: none;
  `;

  const saveNameBtn = document.createElement('button');
  saveNameBtn.style.cssText = `
    padding: 7px 16px;
    background: #3b82f6;
    border: 0;
    border-radius: 8px;
    color: #fff;
    font: 800 11px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  saveNameBtn.textContent = 'SAVE & RELOAD';
  saveNameBtn.addEventListener('click', () => {
    const trimmed = nameInput.value.trim().slice(0, 16);
    if (trimmed.length === 0) {
      flashToast('NAME CANNOT BE EMPTY', '#ef4444');
      return;
    }
    localStorage.setItem(NAME_KEY, trimmed);
    flashToast('RELOADING…', '#86efac');
    setTimeout(() => location.reload(), 500);
  });

  profileRow.appendChild(currentName);
  profileRow.appendChild(nameInput);
  profileRow.appendChild(saveNameBtn);
  profileSection.appendChild(profileRow);
  mountInTab('profile', profileSection);

  // ---- Section: VISUALS ----
  const visualsSection = document.createElement('div');
  visualsSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const visualsTitle = document.createElement('div');
  visualsTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  visualsTitle.textContent = 'VISUALS';
  visualsSection.appendChild(visualsTitle);

  const allyRingsRow = document.createElement('label');
  allyRingsRow.style.cssText = `
    display: flex; align-items: center; gap: 12px;
    cursor: pointer;
    font: 700 12px system-ui; letter-spacing: .5px;
  `;
  const allyRingsCheckbox = document.createElement('input');
  allyRingsCheckbox.type = 'checkbox';
  allyRingsCheckbox.checked = localStorage.getItem('smp.showAllyRings') === '1';
  allyRingsCheckbox.style.cssText = `
    width: 18px; height: 18px; accent-color: #fde047; cursor: pointer;
  `;
  allyRingsCheckbox.addEventListener('change', () => {
    localStorage.setItem(
      'smp.showAllyRings',
      allyRingsCheckbox.checked ? '1' : '0',
    );
    flashToast(
      allyRingsCheckbox.checked ? 'ALLY RINGS ON' : 'ALLY RINGS OFF',
      '#fde047',
    );
  });
  const allyRingsText = document.createElement('span');
  allyRingsText.innerHTML = `Show ally weapon rings when hit
    <em style="opacity:.55; font-weight:600; letter-spacing:0; font-size:10px;">
      (not recommended — visual noise)
    </em>`;
  allyRingsRow.appendChild(allyRingsCheckbox);
  allyRingsRow.appendChild(allyRingsText);
  visualsSection.appendChild(allyRingsRow);
  mountInTab('profile', visualsSection);

  // ---- Section: CONTROLS (touch only) ----
  if (isTouchDevice()) {
    const controlsSection = makeSection('CONTROLS');
    mountInTab('controls', controlsSection);

    // Haptics on/off.
    controlsSection.appendChild(makeToggleRow(
      'Haptics',
      'Vibrate on taps + kills + level-up',
      () => getPrefs().haptics,
      (on) => {
        setPref('haptics', on);
        flashToast(`HAPTICS ${on ? 'ON' : 'OFF'}`, '#fde047');
      },
      subscribeRepaint,
    ));

    // Joystick size — Small / Medium / Large maps to 100/130/160 px.
    controlsSection.appendChild(makeSegmented<number>(
      'Joystick Size',
      [
        { label: 'Small', value: 100 },
        { label: 'Medium', value: 130 },
        { label: 'Large', value: 160 },
      ],
      () => closestNumeric(getPrefs().touchJoystickSize, [100, 130, 160]),
      (v) => setPref('touchJoystickSize', v),
      subscribeRepaint,
    ));

    // Button size — Small / Medium / Large.
    controlsSection.appendChild(makeSegmented<number>(
      'Button Size',
      [
        { label: 'Small', value: 64 },
        { label: 'Medium', value: 78 },
        { label: 'Large', value: 96 },
      ],
      () => closestNumeric(getPrefs().touchButtonSize, [64, 78, 96]),
      (v) => setPref('touchButtonSize', v),
      subscribeRepaint,
    ));

    // Look sensitivity — Slow / Normal / Fast.
    controlsSection.appendChild(makeSegmented<number>(
      'Look Sensitivity',
      [
        { label: 'Slow', value: 0.6 },
        { label: 'Normal', value: 1.0 },
        { label: 'Fast', value: 1.5 },
      ],
      () => closestNumeric(getPrefs().touchLookSens, [0.6, 1.0, 1.5]),
      (v) => setPref('touchLookSens', v),
      subscribeRepaint,
    ));

    // Joystick side — toggling flips both joystick + buttons together.
    controlsSection.appendChild(makeSegmented<'left' | 'right'>(
      'Joystick Side',
      [
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
      ],
      () => getPrefs().touchJoystickAnchor,
      (side) => {
        setPref('touchJoystickAnchor', side);
        setPref('touchButtonsAnchor', side === 'left' ? 'right' : 'left');
      },
      subscribeRepaint,
    ));

    // Layout editor entry point.
    const layoutBtn = document.createElement('button');
    layoutBtn.style.cssText = `
      display: block; width: 100%;
      margin-top: 6px;
      background: rgba(96,165,250,.12); color: #93c5fd;
      border: 1px solid #60a5fa;
      padding: 10px;
      border-radius: 8px;
      font: 800 12px system-ui; letter-spacing: .5px;
      cursor: pointer;
    `;
    layoutBtn.textContent = '📐 Customize Layout';
    layoutBtn.addEventListener('click', () => {
      // Close the settings overlay so the live touch UI is visible
      // for dragging.
      close();
      openTouchLayoutEditor();
    });
    controlsSection.appendChild(layoutBtn);
    controlsSection.appendChild(makeResetButton('controls', 'CONTROLS', flashToast));
  }

  // ---- Section: PERFORMANCE ----
  const perfSection = makeSection('PERFORMANCE');
  mountInTab('display', perfSection);

  perfSection.appendChild(makeSegmented<PerfPreset>(
    'Quality Preset',
    [
      { label: 'Auto',   value: 'auto' },
      { label: 'Low',    value: 'low' },
      { label: 'Medium', value: 'medium' },
      { label: 'High',   value: 'high' },
    ],
    () => getPrefs().perfPreset,
    (v) => {
      setPref('perfPreset', v);
      flashToast(`QUALITY: ${v.toUpperCase()}`, '#fde047');
    },
    subscribeRepaint,
  ));

  // Advanced collapsible.
  const advWrap = document.createElement('div');
  advWrap.style.cssText = `margin-top: 12px;`;
  const advBtn = document.createElement('button');
  advBtn.style.cssText = `
    background: transparent;
    border: 0; padding: 4px 0;
    color: #94a3b8;
    font: 700 11px system-ui; letter-spacing: 1.5px;
    cursor: pointer;
    text-align: left;
  `;
  let advOpen = false;
  const advBody = document.createElement('div');
  advBody.style.cssText = `display: none; margin-top: 8px;`;
  const renderAdvLabel = () => {
    advBtn.textContent = `${advOpen ? '▾' : '▸'} ADVANCED`;
  };
  advBtn.addEventListener('click', () => {
    advOpen = !advOpen;
    advBody.style.display = advOpen ? 'block' : 'none';
    renderAdvLabel();
  });
  renderAdvLabel();
  advWrap.appendChild(advBtn);

  advBody.appendChild(makeSegmented<BloomMode>(
    'Bloom Glow',
    [
      { label: 'Auto', value: 'auto' },
      { label: 'On',   value: 'on' },
      { label: 'Off',  value: 'off' },
    ],
    () => getPrefs().perfBloom,
    (v) => setPref('perfBloom', v),
    subscribeRepaint,
  ));
  advBody.appendChild(makeSegmented<PixelRatioMode>(
    'Pixel Density',
    [
      { label: 'Auto', value: 'auto' },
      { label: '1.0×', value: '1.0' },
      { label: '1.5×', value: '1.5' },
      { label: '2.0×', value: '2.0' },
    ],
    () => getPrefs().perfPixelRatio,
    (v) => setPref('perfPixelRatio', v),
    subscribeRepaint,
  ));
  advBody.appendChild(makeToggleRow(
    'Show FPS',
    'Top-left counter — useful when tuning quality',
    () => getPrefs().showFps,
    (on) => setPref('showFps', on),
    subscribeRepaint,
  ));
  advBody.appendChild(makeToggleRow(
    'Show Debug Stats',
    'Bottom-right green panel — FPS, ping, position, role, room',
    () => getPrefs().showDebugOverlay,
    (on) => setPref('showDebugOverlay', on),
    subscribeRepaint,
  ));
  advBody.appendChild(makeToggleRow(
    'Show Keybind Hints',
    'Bottom-left cheat sheet — WASD, hood, debug hotkeys',
    () => getPrefs().showControlsHelper,
    (on) => setPref('showControlsHelper', on),
    subscribeRepaint,
  ));
  advWrap.appendChild(advBody);
  perfSection.appendChild(advWrap);
  perfSection.appendChild(makeResetButton('performance', 'PERFORMANCE', flashToast));

  // ---- Section: ACCESSIBILITY ----
  const accessSection = makeSection('ACCESSIBILITY');
  mountInTab('display', accessSection);

  const motionTiers: Array<{ label: string; value: MotionTier }> = [
    { label: 'Off',     value: 'off' },
    { label: 'Subtle',  value: 'subtle' },
    { label: 'Normal',  value: 'normal' },
    { label: 'Intense', value: 'intense' },
  ];
  accessSection.appendChild(makeSegmented<MotionTier>(
    'Camera Shake',
    motionTiers,
    () => getPrefs().motionShake,
    (v) => setPref('motionShake', v),
    subscribeRepaint,
  ));
  accessSection.appendChild(makeSegmented<MotionTier>(
    'Hit Pause',
    motionTiers,
    () => getPrefs().motionHitstop,
    (v) => setPref('motionHitstop', v),
    subscribeRepaint,
  ));

  const densityTiers: Array<{ label: string; value: DensityTier }> = [
    { label: 'Off',    value: 'off' },
    { label: 'Sparse', value: 'sparse' },
    { label: 'Normal', value: 'normal' },
    { label: 'Lush',   value: 'lush' },
  ];
  accessSection.appendChild(makeSegmented<DensityTier>(
    'Particle Density',
    densityTiers,
    () => getPrefs().particleDensity,
    (v) => setPref('particleDensity', v),
    subscribeRepaint,
  ));
  // Damage-number visibility — All / Mine / Off. 6-bot fights with
  // "all" can read as visual noise, so the player can clamp to their
  // own kills/deaths or kill the floating numbers entirely (juice +
  // bloom kick stay even at Off so combat still feels punchy).
  accessSection.appendChild(makeSegmented<DamageNumbersMode>(
    'Damage Numbers',
    [
      { label: 'All',  value: 'all' },
      { label: 'Mine', value: 'mine' },
      { label: 'Off',  value: 'off' },
    ],
    () => getPrefs().damageNumbers,
    (v) => setPref('damageNumbers', v),
    subscribeRepaint,
  ));
  // Colorblind mode — high-contrast body class. Moved here from the
  // pause menu (playtest 2026-04-28) so all preferences live in one
  // place. Backed by the same `smp.cb` localStorage key the old pause
  // menu wrote to so existing players keep their setting.
  accessSection.appendChild(makeToggleRow(
    'Colorblind Mode',
    'High-contrast palette adjustments across HUD elements',
    () => {
      try { return localStorage.getItem('smp.cb') === '1'; } catch { return false; }
    },
    (on) => {
      try { localStorage.setItem('smp.cb', on ? '1' : '0'); } catch { /* ignore */ }
      document.body.classList.toggle('cb-mode', on);
    },
  ));
  accessSection.appendChild(makeResetButton('accessibility', 'ACCESSIBILITY', flashToast));

  // ---- Section: HUD ----
  // Per-element on/off toggles for every overlay that competes for screen
  // real estate. Defaults match the as-shipped HUD; turning anything off
  // immediately hides it via the smp:hud-toggle event (no reload).
  const hudSection = document.createElement('div');
  hudSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const hudTitle = document.createElement('div');
  hudTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  hudTitle.textContent = 'HUD ELEMENTS';
  hudSection.appendChild(hudTitle);

  for (const t of HUD_TOGGLES) {
    const row = document.createElement('label');
    row.style.cssText = `
      display: flex; align-items: center; gap: 10px;
      padding: 6px 4px;
      font: 700 12px system-ui; color: #cbd5e1;
      cursor: pointer;
    `;
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = isHudVisible(t.key);
    cb.style.cssText = 'width:16px;height:16px;accent-color:#fde047;cursor:pointer;';
    cb.addEventListener('change', () => {
      setHudVisible(t.key, cb.checked);
      flashToast(`${t.label.toUpperCase()} ${cb.checked ? 'ON' : 'OFF'}`, '#fde047');
    });
    const label = document.createElement('span');
    label.innerHTML = `${t.label}${t.hint ? `
      <em style="opacity:.55;font-weight:600;letter-spacing:0;font-size:10px;">
        — ${t.hint}
      </em>` : ''}`;
    row.appendChild(cb);
    row.appendChild(label);
    hudSection.appendChild(row);
  }
  mountInTab('profile', hudSection);

  // ---- Section: HELP ----
  const helpSection = document.createElement('div');
  helpSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const helpTitle = document.createElement('div');
  helpTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  helpTitle.textContent = 'HELP';
  helpSection.appendChild(helpTitle);

  const replayBtn = document.createElement('button');
  replayBtn.style.cssText = `
    padding: 9px 18px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #e5e7eb;
    font: 800 12px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  replayBtn.textContent = '▶ REPLAY INTRO TUTORIAL';
  replayBtn.addEventListener('click', () => {
    localStorage.removeItem(INTRO_KEY);
    close();
    callbacks.onReplayIntro?.();
  });
  helpSection.appendChild(replayBtn);

  // Entrance video replays — two buttons, one per cut. Same layout
  // as the tutorial replay above. Each closes Settings, then plays
  // the chosen video over whatever's on screen. The video player
  // guards against overlap, so spam-clicks are no-ops.
  const entranceRow = document.createElement('div');
  entranceRow.style.cssText = `
    display: flex; gap: 10px; flex-wrap: wrap;
    margin-top: 10px;
  `;
  const makeEntranceBtn = (label: string, src: string): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.style.cssText = `
      padding: 9px 18px;
      background: rgba(15,23,42,.8);
      border: 1px solid #475569;
      border-radius: 8px;
      color: #e5e7eb;
      font: 800 12px system-ui; letter-spacing: 1px;
      cursor: pointer;
    `;
    btn.textContent = label;
    btn.addEventListener('click', () => {
      close();
      // Dynamic import keeps the entrance video out of the settings-
      // modal chunk; it's already in the boot graph anyway.
      void import('./intro_video').then(({ showIntroVideo }) => {
        void showIntroVideo(src);
      });
    });
    return btn;
  };
  entranceRow.appendChild(makeEntranceBtn('▶ FIRST ENTRANCE',  '/intro/entrance.mp4'));
  entranceRow.appendChild(makeEntranceBtn('▶ SECOND ENTRANCE', '/intro/entrance2.mp4'));
  helpSection.appendChild(entranceRow);

  mountInTab('help', helpSection);
  // First paint — hide all sections except the active tab.
  paintTabs();

  // DEBUG section removed from Settings 2026-04-28 — bloated the
  // modal even in DEV. Power users still reach the dev console via
  // F8 and the test panel via the `?dev=1` URL flag; the section
  // file `src/dev/settings_debug_section.ts` is left in place for
  // future re-introduction.

  // ---- Close button ----
  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'CLOSE [ESC]';
  closeBtn.style.cssText = `
    margin-top: 6px; padding: 9px 24px;
    border-radius: 8px;
    border: 1px solid #475569;
    background: rgba(15,23,42,.85);
    color: #e5e7eb;
    cursor: pointer;
    font: 800 12px system-ui; letter-spacing: 1.5px;
  `;
  overlay.appendChild(closeBtn);

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    removeEventListener('keydown', onCapture, true);
    removeEventListener('keydown', onKeyTop);
    clearTimeout(toastTimer);
    for (const u of prefsUnsubs) u();
    prefsUnsubs.length = 0;
    overlay.remove();
    onClose?.();
  }
  const onKeyTop = (e: KeyboardEvent) => {
    // Only let Escape close the modal if we're NOT mid-rebind —
    // the rebind capture handles its own Escape-cancel.
    if (listening) return;
    if (e.code === 'Escape') { e.preventDefault(); close(); }
  };
  addEventListener('keydown', onKeyTop);
  closeBtn.addEventListener('click', close);

  document.body.appendChild(overlay);
  return close;
}

const SETTINGS_CSS_ID = 'smp-settings-css';
function ensureSettingsCss(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SETTINGS_CSS_ID)) return;
  const s = document.createElement('style');
  s.id = SETTINGS_CSS_ID;
  s.textContent = `
    .smp-settings-overlay {
      animation: smp-settings-fade-in .22s ease-out both;
    }
    .smp-settings-overlay > * {
      animation: smp-settings-rise .26s cubic-bezier(.18,1.2,.4,1) both;
    }
    .smp-settings-overlay > *:nth-child(1) { animation-delay: .02s; }
    .smp-settings-overlay > *:nth-child(2) { animation-delay: .06s; }
    .smp-settings-overlay > *:nth-child(3) { animation-delay: .10s; }
    @keyframes smp-settings-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes smp-settings-rise {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(s);
}

// ---- Helpers for the new sections ----

function makeSection(title: string): HTMLDivElement {
  const section = document.createElement('div');
  section.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const head = document.createElement('div');
  head.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  head.textContent = title;
  section.appendChild(head);
  return section;
}

function makeToggleRow(
  label: string,
  hint: string,
  getValue: () => boolean,
  setValue: (on: boolean) => void,
  subscribeToChanges?: (repaint: () => void) => void,
): HTMLLabelElement {
  const row = document.createElement('label');
  row.style.cssText = `
    display: flex; align-items: center; gap: 12px;
    cursor: pointer;
    font: 700 12px system-ui; letter-spacing: .5px;
    margin-bottom: 10px;
  `;
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = getValue();
  cb.style.cssText = `
    width: 18px; height: 18px; accent-color: #fde047; cursor: pointer;
  `;
  cb.addEventListener('change', () => setValue(cb.checked));
  if (subscribeToChanges) subscribeToChanges(() => { cb.checked = getValue(); });
  const text = document.createElement('span');
  text.innerHTML = `${label}
    ${hint ? `<em style="opacity:.55; font-weight:600; letter-spacing:0; font-size:10px;">— ${hint}</em>` : ''}`;
  row.appendChild(cb);
  row.appendChild(text);
  return row;
}

function makeSegmented<T>(
  label: string,
  options: ReadonlyArray<{ label: string; value: T }>,
  getValue: () => T,
  setValue: (v: T) => void,
  subscribeToChanges?: (repaint: () => void) => void,
): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = `
    display: flex; align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  `;
  const lbl = document.createElement('span');
  lbl.style.cssText = `
    font: 700 12px system-ui; letter-spacing: .5px;
    color: #cbd5e1;
    min-width: 130px;
  `;
  lbl.textContent = label;
  wrap.appendChild(lbl);

  const group = document.createElement('div');
  group.style.cssText = `
    display: inline-flex; gap: 4px;
    background: rgba(15,23,42,.85);
    border: 1px solid rgba(255,255,255,.1);
    border-radius: 10px;
    padding: 3px;
  `;

  const buttons: HTMLButtonElement[] = [];
  const repaint = () => {
    const cur = getValue();
    for (let i = 0; i < buttons.length; i++) {
      const active = options[i].value === cur;
      buttons[i].style.background = active ? '#fde047' : 'transparent';
      buttons[i].style.color = active ? '#0b0d12' : '#cbd5e1';
      buttons[i].style.fontWeight = active ? '900' : '700';
    }
  };

  for (const opt of options) {
    const btn = document.createElement('button');
    btn.textContent = opt.label;
    btn.style.cssText = `
      border: 0;
      background: transparent;
      color: #cbd5e1;
      padding: 6px 12px;
      border-radius: 7px;
      font: 700 11px system-ui;
      letter-spacing: 1px;
      cursor: pointer;
      transition: background .15s ease, color .15s ease;
    `;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      setValue(opt.value);
      repaint();
    });
    buttons.push(btn);
    group.appendChild(btn);
  }
  repaint();
  if (subscribeToChanges) subscribeToChanges(repaint);
  wrap.appendChild(group);
  return wrap;
}

function makeResetButton(
  section: 'controls' | 'performance' | 'accessibility',
  label: string,
  flashToast: (msg: string, color?: string) => void,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.style.cssText = `
    display: block; margin-top: 12px;
    background: transparent;
    color: #94a3b8;
    border: 1px solid rgba(255,255,255,.12);
    padding: 6px 12px;
    border-radius: 8px;
    font: 800 10px system-ui, sans-serif; letter-spacing: 1.5px;
    cursor: pointer;
  `;
  btn.textContent = '↺ RESTORE DEFAULTS';
  btn.addEventListener('click', () => {
    resetSection(section);
    flashToast(`${label} RESET`, '#fde047');
  });
  return btn;
}

function closestNumeric(v: number, options: number[]): number {
  let best = options[0];
  let bestD = Math.abs(v - best);
  for (let i = 1; i < options.length; i++) {
    const d = Math.abs(v - options[i]);
    if (d < bestD) { best = options[i]; bestD = d; }
  }
  return best;
}
